import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, Modal } from "react-native";
import { useApiWithLoading } from '../../../../../hooks/useApiWithLoading';
import GradientBackground from '../../../../uiComponents/GradientBackground';
import { postFeeLookup } from '../../../../../services/common.service';
import { useSessionStore } from '../../../../../../../state/src/useSessionStore';
import type { SessionState } from '../../../../../../../state/src/useSessionStore';
import { ProductType } from '../../../../../view-models/productType';

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

export default function SendingAmountEntryStep({ onValidationChange }: Props) {
  const { t } = useTranslation();
  const { executeWithLoading } = useApiWithLoading();
  const sendAmount = useSessionStore((s: SessionState) => s.currentSendAmount);
  const receiveAmount = useSessionStore((s: SessionState) => s.theyReceiveAmount);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [receiveCurrency, setReceiveCurrency] = useState<string>("");
  const [willCallOptions, setWillCallOptions] = useState<Array<{ currency: string; rate: number }>>([]);
  const [selectedReceiveCurrency, setSelectedReceiveCurrency] = useState<string | null>(null);
  const setSelectedReceiveCurrencyGlobal = useSessionStore((s: SessionState) => s.setSelectedReceiveCurrency);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const setCurrentSendAmount = useSessionStore((s: SessionState) => (s as any).setCurrentSendAmount);
  const setTheyReceiveAmount = useSessionStore((s: SessionState) => (s as any).setTheyReceiveAmount);
  const [feeAmount, setFeeAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [showCurrencyModal, setShowCurrencyModal] = useState<boolean>(false);

  const isValid = sendAmount !== "0.00";
    
    useEffect(() => {
      if (onValidationChange) {
        onValidationChange(isValid);
      }
    }, [isValid, onValidationChange]);
      

  const parseAmount = (text: string): string => {
    // allow only digits and one optional decimal point
    const cleaned = text.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }
    return cleaned;
  };

  const computeReceiveFromSend = (sendStr: string | undefined, rate: number) => {
    const s = parseFloat(sendStr || "0");
    if (!isFinite(s)) return "0.00";
    const r = s * (rate || 0);
    return r.toFixed(2);
  };

  const computeSendFromReceive = (recvStr: string, rate: number) => {
    const r = parseFloat(recvStr || "0");
    if (!isFinite(r) || !rate) return "0.00";
    const s = r / rate;
    return s.toFixed(2);
  };

  // Fee lookup on mount and when selection changes
useEffect(() => {
  const doLookup = async () => {
    // Always show loader, even if IDs are missing
    await executeWithLoading(async () => {
      if (!agentId || !unitProfileId || !selectedDestinationCountryCode) {
        // Just wait until dependencies are available
        return;
      }

      try {
        const payload: any = {
          agentID: agentId,
          unitProfileID: unitProfileId,
          sendCurrency: "USD",
          destinationCountry: selectedDestinationCountryCode,
          allOptions: true,
          productType: ProductType.SEND,
          amountExcludingFee: 1,
          amount: 1,
        };

        if (
          (selectedDestinationCountryCode === "USA" || selectedDestinationCountryCode === "CAN") &&
          selectedDestinationStateAbbr
        ) {
          payload.destinationState = selectedDestinationStateAbbr;
        }

        const res = await postFeeLookup(payload);
        console.log('postfeelookup==>', res)

        let inner: any = res;
        if (typeof res?.data === "string") {
          try {
            inner = JSON.parse(res.data);
          } catch {
            inner = res;
          }
        } else if (typeof res?.data === "object") {
          inner = res.data;
        }

        console.log('inner==>', inner)
        const feeInfosArr: any[] = Array.isArray(inner?.FeeInfos) ? inner.FeeInfos : [];
        console.log('feeInfosArr==>', feeInfosArr)
        const willCallInfos = feeInfosArr.filter((f: any) => {
          const opt = (f?.DeliveryOption ?? f?.DeliveryOptId ?? "")
            .toString()
            .toUpperCase();
          const name = (f?.DeliveryOptDisplayName ?? "")
            .toString()
            .toUpperCase();
          return opt === "WILL_CALL" || name.includes("WILL CALL");
        });
        console.log('willCallInfos==>', willCallInfos)

        const optionsMap = new Map<string, number>();
        for (const info of willCallInfos.length ? willCallInfos : feeInfosArr) {
          const rateVal =
            info?.ValidExchangeRate || info?.EstimatedExchangeRate || 1;
          const rc = info?.ReceiveAmounts?.ValidCurrencyIndicator
            ? info?.ValidReceiveCurrency || ""
            : info?.EstimatedReceiveCurrency ||
              info?.ValidReceiveCurrency ||
              "";
          const currency = (rc || "").toString();
          if (currency && !optionsMap.has(currency)) {
            optionsMap.set(currency, Number(rateVal) || 1);
          }
        }
        console.log('optionsMap==>', optionsMap)
        const options = Array.from(optionsMap.entries()).map(
          ([currency, rate]) => ({ currency, rate })
        );
        setWillCallOptions(options);
        console.log('Finally setOption==>', options)

        // if (selectedSendOption) return;

        const defaultOption =
          options.find((o) => o.currency !== "USD") ||
          options[0] ||
          { currency: "", rate: 1 };

        console.log('defaultOption setOption==>', defaultOption)
        setSelectedReceiveCurrency(defaultOption.currency || null);
        setSelectedReceiveCurrencyGlobal(defaultOption.currency || null);
        setExchangeRate(defaultOption.rate || 1);
        setReceiveCurrency(defaultOption.currency || "");
        console.log('setReceiveCurrency==>', defaultOption.currency)
        setTheyReceiveAmount(
          computeReceiveFromSend(sendAmount, defaultOption.rate || 1)
        );
      } catch (e) {
        console.error("Fee lookup failed in SendAmountEntryStep", e);
      }
    }, "Processing...");
  };

  doLookup();
}, [
  agentId,
  unitProfileId,
  selectedDestinationCountryCode,
  selectedDestinationStateAbbr,
  selectedSendOption,
]);

  // Show currency chooser on load when multiple options are available and none selected
  useEffect(() => {
    if (willCallOptions.length > 1 && !selectedReceiveCurrency) {
      setShowCurrencyModal(true);
    }
  }, [willCallOptions, selectedReceiveCurrency]);

  // // Fee lookup on mount and when selection changes
  // useEffect(() => {
  //   const doLookup = async () => {
  //     await executeWithLoading(async () => {
  //     if (!agentId || !unitProfileId || !selectedCountryCode) {
  //       console.log('NO AGENT ID SO RETURNNNNN')
  //       return;
  //     }
  //     try {
        
  //       const payload: any = {
  //         agentID: agentId,
  //         unitProfileID: unitProfileId,
  //         sendCurrency: 'USD',
  //         destinationCountry: selectedCountryCode,
  //         allOptions: true,
  //         productType: ProductType.SEND,
  //         amountExcludingFee: 1,
  //         amount: 1
  //       };
  //       if ((selectedCountryCode === 'USA' || selectedCountryCode === 'CAN') && selectedStateAbbr) {
  //         payload.destinationState = selectedStateAbbr;
  //       }
  //       const res = await postFeeLookup(payload);
  //       // derive rate from WILL_CALL delivery option if present
  //       let inner: any = res;
  //       if (typeof res?.data === 'string') {
  //         try { inner = JSON.parse(res.data); } catch { inner = res; }
  //       } else if (typeof res?.data === 'object') {
  //         inner = res.data;
  //       }
  //       const feeInfosArr: any[] = Array.isArray(inner?.FeeInfos) ? inner.FeeInfos : [];
  //       const willCallInfos = feeInfosArr.filter((f: any) => {
  //         const opt = (f?.DeliveryOption ?? f?.DeliveryOptId ?? '').toString().toUpperCase();
  //         const name = (f?.DeliveryOptDisplayName ?? '').toString().toUpperCase();
  //         return opt === 'WILL_CALL' || name.includes('WILL CALL');
  //       });
  //       // Build options by unique receive currency
  //       const optionsMap = new Map<string, number>();
  //       for (const info of (willCallInfos.length ? willCallInfos : feeInfosArr)) {
  //         const rateVal = info?.ValidExchangeRate || info?.EstimatedExchangeRate || 1;
  //         const rc = info?.ReceiveAmounts?.ValidCurrencyIndicator
  //           ? (info?.ValidReceiveCurrency || '')
  //           : (info?.EstimatedReceiveCurrency || info?.ValidReceiveCurrency || '');
  //         const currency = (rc || '').toString();
  //         if (currency) {
  //           if (!optionsMap.has(currency)) {
  //             optionsMap.set(currency, Number(rateVal) || 1);
  //           }
  //         }
  //       }
  //       const options = Array.from(optionsMap.entries()).map(([currency, rate]) => ({ currency, rate }));
  //       setWillCallOptions(options);

  //       // If a send option is already selected from previous step, do NOT override its rate/currency
  //       if (selectedSendOption) {
  //         return;
  //       }

  //       // Otherwise choose default option: prefer non-USD, else first available
  //       const defaultOption = options.find(o => o.currency !== 'USD') || options[0] || { currency: '', rate: 1 };
  //       setSelectedReceiveCurrency(defaultOption.currency || null);
  //       setSelectedReceiveCurrencyGlobal(defaultOption.currency || null);
  //       setExchangeRate(defaultOption.rate || 1);
  //       setReceiveCurrency(defaultOption.currency || "");
  //       setReceiveAmount(computeReceiveFromSend(sendAmount, defaultOption.rate || 1));
  //     } catch (e) {
  //       console.error('Fee lookup failed in SendAmountEntryStep', e);
  //     }
  //     }, "Processing...");
  //   };
  //   doLookup();
  // }, [agentId, unitProfileId, selectedCountryCode, selectedStateAbbr, selectedSendOption]);

  // If user has already selected a send option in previous step, honor its rate/currency
  useEffect(() => {
    if (!selectedSendOption) return;
    const rateVal = selectedSendOption?.ValidExchangeRate || selectedSendOption?.EstimatedExchangeRate || 1;
    const rc = selectedSendOption?.ReceiveAmounts?.ValidCurrencyIndicator
      ? (selectedSendOption?.ValidReceiveCurrency || '')
      : (selectedSendOption?.EstimatedReceiveCurrency || selectedSendOption?.ValidReceiveCurrency || '');
    const rateNum = Number(rateVal) || 1;
    setExchangeRate(rateNum);
    setReceiveCurrency(rc || '');
    setSelectedReceiveCurrency(rc || null);
    setSelectedReceiveCurrencyGlobal(rc || null);
    setTheyReceiveAmount(computeReceiveFromSend(sendAmount, rateNum));

    const numericSend = parseFloat(sendAmount || "0");
    if (!isFinite(numericSend) || numericSend <= 0) {
      setFeeAmount(0);
      setTotalAmount(0);
    } else {
      const fees = Number(selectedSendOption?.SendAmounts?.TotalDiscountAmount || 0) + Number(selectedSendOption?.SendAmounts?.TotalSendFees || 0);
      setFeeAmount(fees);
      const baseTotal = Number(selectedSendOption?.TotalAmount || 0);
      setTotalAmount(baseTotal + fees);
    }
  }, [selectedSendOption]);

  // Recalculate fees/total when user edits the send amount
  useEffect(() => {
    const numericSend = parseFloat(sendAmount || "0");
    if (!selectedSendOption) {
      setFeeAmount(0);
      setTotalAmount(0);
      return;
    }
    if (!isFinite(numericSend) || numericSend <= 0) {
      setFeeAmount(0);
      setTotalAmount(0);
      return;
    }
    const computedFees = Number(selectedSendOption?.SendAmounts?.TotalDiscountAmount || 0) + Number(selectedSendOption?.SendAmounts?.TotalSendFees || 0);
    setFeeAmount(computedFees);
    const baseTotal = Number(selectedSendOption?.TotalAmount || 0);
    setTotalAmount(baseTotal + computedFees);
  }, [sendAmount, selectedSendOption]);

  console.log((Number(selectedSendOption?.TotalAmount || 0) + feeAmount).toFixed(2) , 'total amount 1');
  console.log(selectedSendOption);

  return (
    <View style={styles.container}>
      {/* <Text style={styles.title}>Confirm your PIN</Text> */}
      <Text style={styles.subtitle}>{t("AMOUNT_ENTRY_SUBTITLE")}</Text>
      {!!selectedSendOption?.DeliveryOptDisplayName && (
        <Text style={{ fontSize: 13, color: '#6B7280', marginTop: -8, marginBottom: 12 }}>
          {selectedSendOption.DeliveryOptDisplayName}
        </Text>
      )}
      {/* You send */}
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>{t("AMOUNT_ENTRY_YOU_SEND")}</Text>
                  <View style={styles.amountInput}>
                    <TextInput
                      autoFocus
                      style={styles.amountText}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      value={sendAmount}
                      onChangeText={(t) => {
                        const next = parseAmount(t);
                        // setSendAmount(next);
                        setCurrentSendAmount?.(next);
                        setTheyReceiveAmount(computeReceiveFromSend(next, exchangeRate));
                      }}
                    />
                    <Text style={styles.currencyText}>USD</Text>
                  </View>
                </View>
                {/* Currency options moved to bottom-sheet modal */}
      
                {/* They receive */}
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>{t("AMOUNT_ENTRY_THEY_RECEIVE")}</Text>
                  <View style={styles.amountInput}>
                    <TextInput
                      style={styles.amountText}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      value={receiveAmount}
                      onChangeText={(t) => {
                        const next = parseAmount(t);
                        setTheyReceiveAmount(next);
                        setCurrentSendAmount(computeSendFromReceive(next, exchangeRate));
                      }}
                    />
                    <TouchableOpacity disabled={willCallOptions.length <= 1} onPress={() => setShowCurrencyModal(true)}>
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={styles.currencyText}>{receiveCurrency || '—'}</Text>
                        {willCallOptions?.length > 1 && (
                          <Text style={styles.dropdownArrow}>▼</Text>
                        )}
                      </View>
                    </TouchableOpacity>
                  </View>
                </View>
                {/* Fees and CTA */}
                        {/* <GradientBackground style={styles.card}>
                            <View style={styles.row}>
                              <Text style={styles.feeLabel}>{t("AMOUNT_ENTRY_FEES")}</Text>
                              <View style={{ flexDirection: 'row'}}>
                                <Text style={styles.oldPrice}>{feeAmount.toFixed(2)} {(selectedSendOption?.SendAmounts?.SendCurrency || 'USD')}</Text>
                              </View>
                            </View>
                            <View style={styles.row}>
                              <Text style={styles.feeLabel}>{t("AMOUNT_ENTRY_TOTAL")}</Text>
                              <View style={{ flexDirection: 'row'}}>
                                <Text style={styles.oldPrice}>{(Number(selectedSendOption?.TotalAmount || 0) + feeAmount).toFixed(2)} {(selectedSendOption?.SendAmounts?.SendCurrency || 'USD')}</Text>
                              </View>
                            </View>
                
                          </GradientBackground> */}
      
      {/* Bottom-sheet currency chooser */}
      <Modal visible={showCurrencyModal} animationType="slide" transparent={true}>
        <View style={styles.modalOverlay}>
          <View style={styles.bottomSheet}>
            <View style={styles.modalHeaderRow}>
              <Text style={styles.modalTitle}>{t("AMOUNT_ENTRY_RECEIVE_CURRENCY")}</Text>
              <TouchableOpacity onPress={() => setShowCurrencyModal(false)}>
                <Text style={styles.modalClose}>✕</Text>
              </TouchableOpacity>
            </View>
            {willCallOptions.map((opt) => (
              <TouchableOpacity
                key={opt.currency}
                style={styles.currencyModalOptionRow}
                onPress={() => {
                  setSelectedReceiveCurrency(opt.currency);
                  setSelectedReceiveCurrencyGlobal(opt.currency);
                  setReceiveCurrency(opt.currency);
                  const selectedCurrencyUpper = (selectedSendOption?.ReceiveAmounts?.ValidCurrencyIndicator
                    ? (selectedSendOption?.ValidReceiveCurrency || '')
                    : (selectedSendOption?.EstimatedReceiveCurrency || selectedSendOption?.ValidReceiveCurrency || '')).toString().toUpperCase();
                  const isSelectedCurrency = selectedSendOption && (selectedCurrencyUpper === opt.currency.toUpperCase());
                  const rateToUse = isSelectedCurrency
                    ? Number(selectedSendOption?.ValidExchangeRate || selectedSendOption?.EstimatedExchangeRate || opt.rate)
                    : opt.rate;
                  setExchangeRate(rateToUse);
                  setTheyReceiveAmount(computeReceiveFromSend(sendAmount, rateToUse));
                  setShowCurrencyModal(false);
                }}
              >
                <View style={[styles.radioOuter, selectedReceiveCurrency === opt.currency && styles.radioOuterSelected]}>
                  {selectedReceiveCurrency === opt.currency && <View style={styles.radioInner} />}
                </View>
                <Text style={{ marginLeft: 12, fontSize: 16 }}>{opt.currency}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </Modal>

    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    justifyContent: "flex-start",
    marginTop: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 6,
    color: "#111827",
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
  },
    exchangeRate: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: '500',
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 15,
    // borderRadius: 12,
    // borderWidth: 1,
    // borderColor: '#e0e0e0',
  },
  amountText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  amountTextGreen: {
    color: '#4CAF50',
  },
  currencyText: {
    fontSize: 16,
    color: '#666',
  },
  dropdownArrow: {
    marginLeft: 4,
    fontSize: 10,
    color: '#666'
  },
  currencyOptionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  currencyOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    width: '48%'
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: { borderColor: '#000' },
  radioInner: { width: 9, height: 9, borderRadius: 4.5, backgroundColor: '#000' },
  readOnlyInput: {
    backgroundColor: '#fafafa',
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 20,
  },
  pinContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  pinInput: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    marginRight: 24
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  card: {
    alignItems: "center",
    // width: 327,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: 16,
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: "black",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    // marginHorizontal: 30,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.4)'
  },
  bottomSheet: {
    backgroundColor: '#fff',
    padding: 20,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700'
  },
  modalClose: {
    fontSize: 18,
    color: '#666'
  },
  currencyModalOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB'
  }
});
